import type { FocusArea, Modality } from "~/lib/matching/taxonomy";

/**
 * Display labels for current and historical work-area keys.
 * Must cover every seeded key in FOCUS_AREAS.
 */
export const FOCUS_AREA_LABELS: Record<FocusArea, string> = {
  psychotic_experiences: "Psihotična iskustva",
  suicidality: "Suicidalnost",
  self_destructive_behavior: "Autodestruktivna ponašanja",
  behavioral_difficulties: "Teškoće u ponašanju",
  borderline_personality_organization: "Granična organizacija ličnosti",
  obesity_support: "Psihološka podrška osobama sa gojaznošću",
  intrusive_thoughts: "Neželjene i nametljive misli",
  attention_difficulties: "Teškoće sa pažnjom i fokusom",
  impulsivity: "Impulsivnost",
  mood_difficulties: "Teškoće sa raspoloženjem",

  work_with_adults: "Rad sa odraslima",
  work_with_adolescents: "Rad sa adolescentima",
  work_with_children: "Rad sa decom",
  work_with_parents: "Rad sa roditeljima",
  work_with_couples: "Rad sa parovima",
  work_with_lgbtq: "Rad sa LGBTQ osobama",
  work_with_older_adults: "Rad sa starijim osobama",
  work_with_disabled_people: "Rad sa osobama sa invaliditetom",
  work_with_neurodivergent_people: "Rad sa neurodivergentnim osobama",
  work_with_students: "Rad sa studentima",
  work_with_people_abroad: "Rad sa osobama koje žive u inostranstvu",
  work_with_caregivers: "Rad sa osobama koje brinu o članu porodice",
  work_with_perinatal_clients: "Rad sa trudnicama i osobama posle porođaja",

  separation_divorce: "raskid i razvod",
  jealousy: "ljubomora",
  non_monogamy: "Nemonogamni odnosi",
  adoption: "Usvajanje",
  blended_families: "odnosi u porodici sa decom iz prethodnih veza",
  involuntary_childlessness: "život bez dece mimo sopstvene želje",
  ptsd: "Posttraumatski stresni poremećaj (PTSP)",
  complex_ptsd: "Kompleksni PTSP",
  dissociation: "Disocijacija",
  anorexia: "Anoreksija",
  bulimia: "Bulimija",
  binge_eating: "Poremećaj prejedanja",
  arfid: "Izbegavajući/restriktivni poremećaj unosa hrane (ARFID)",
  hair_pulling: "Čupanje kose i dlaka (trihotilomanija)",
  skin_picking: "Kompulzivno čačkanje kože",
  body_dysmorphia: "Telesni dismorfni poremećaj",
  problematic_internet_use: "Problematična upotreba interneta",
  problematic_pornography_use: "Problematična upotreba pornografije",
  hoarding: "Kompulzivno gomilanje stvari",
  discrimination: "Iskustva diskriminacije",
  financial_distress: "stres zbog finansijskih teškoća",
  spirituality: "duhovnost i religijska pitanja",
  infertility: "Teškoće sa začećem i neplodnost",
  pregnancy_loss: "Gubitak trudnoće",
  chronic_pain: "Život sa hroničnim bolom",
  bullying: "vršnjačko nasilje i mobing",
  caregiver_stress: "briga o bolesnom ili zavisnom članu porodice",
  menopause: "menopauza i perimenopauza",
  perfectionism: "perfekcionizam",
  psychosomatic: "Psihosomatske teškoće",
  sexual_difficulties: "Seksualne teškoće",
  perinatal: "Trudnoća i period posle porođaja",
  chronic_illness: "Život sa bolešću",
  adhd: "ADHD",
  autism: "Autizam",
  personality_disorders: "Poremećaji ličnosti",
  bpd: "Granični poremećaj ličnosti (BPD)",
  bipolar: "Bipolarni poremećaj",
  violence_abuse: "Nasilje i zlostavljanje",
  migration_adjustment: "Migracija i život u inostranstvu",
  health_anxiety: "zdravstvena anksioznost",
  learning_difficulties: "Teškoće u učenju",
  anxiety: "Anksioznost",
  panic_attacks: "panični napadi",
  depression: "Depresija",
  burnout: "sagorevanje",
  stress: "Stres",
  trauma: "Trauma",
  grief_loss: "Gubitak i žalovanje",
  ocd: "Opsesivno-kompulzivni poremećaj (OKP)",
  addictions: "Zavisnosti",
  gambling: "Zavisnost od kockanja",
  substance_addictions: "Zavisnost od alkohola i psihoaktivnih supstanci",
  self_esteem: "Samopouzdanje i samokritičnost",
  boundaries: "granice",
  relationships: "Odnosi, komunikacija i granice",
  communication: "komunikacija",
  emotional_regulation: "Emocije i raspoloženje",
  phobias: "fobije",
  life_crises: "Životne promene",
  parenting: "Roditeljstvo",
  identity: "identitet",
  anger: "bes",
  shame_guilt: "stid i krivica",
  lgbtq: "LGBTQ teme",
  adolescents: "adolescenti",
  couples_issues: "Partnerski odnosi",
  eating_disorders: "Poremećaji ishrane",
  sleep_problems: "Problemi sa spavanjem",
  narcissistic_abuse: "narcističko zlostavljanje",
  social_anxiety: "socijalna anksioznost",
  procrastination: "Motivacija, fokus i obaveze",
  decision_making: "donošenje odluka",
  self_development: "Lični razvoj i identitet",
  childhood_development: "Razvojne teškoće kod dece",
  family_issues: "Porodični odnosi",
  work_issues: "Posao i sagorevanje",
  motivation: "nedostatak motivacije",
  loneliness: "usamljenost",
};

/**
 * Sentence case: these render as standalone chips/labels. Must cover every
 * value in the shared modality label vocabulary.
 */
export const MODALITY_LABELS: Record<Modality, string> = {
  kbt: "KBT pristup",
  rebt: "REBT / KBT pristup",
  gestalt: "Geštalt pristup",
  act: "ACT pristup",
  cft: "CFT pristup",
  dbt: "DBT pristup",
  psychodynamic: "Psihodinamski pristup",
  integrative: "Integrativni pristup",
  systemic: "Sistemski porodični pristup",
  emdr: "EMDR pristup",
  mindfulness: "Mindfulness pristup",
  other: "Drugi terapijski pristup",
  unknown: "Terapijski pristup nije naveden",
};
